import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Button,
} from '@mui/material';
import {
  CheckCircle,
} from '@mui/icons-material';
import { sessionAPI } from '../services/api';
import { useLocale } from '../contexts/LocaleContext';
import { trackAssessmentComplete } from '../utils/analytics';

// Helper function to format date from various formats
const formatCompletedDate = (dateValue: any): string => {
  try {
    if (!dateValue) return new Date().toLocaleDateString();
    
    // Handle Firestore Timestamp
    if (dateValue && typeof dateValue === 'object' && dateValue.toDate) {
      return dateValue.toDate().toLocaleDateString();
    }
    
    // Handle ISO string or timestamp
    if (typeof dateValue === 'string' || typeof dateValue === 'number') {
      const date = new Date(dateValue);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString();
      }
    }
    
    // Fallback
    return new Date().toLocaleDateString();
  } catch (error) {
    console.warn('Error formatting date:', error);
    return new Date().toLocaleDateString();
  }
};

const ResultsPage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const { t } = useLocale();

  const { data: session, isLoading, error } = useQuery(
    ['session', sessionId],
    () => sessionAPI.getById(sessionId!),
    {
      enabled: !!sessionId,
    }
  );

  // Track results page view
  useEffect(() => {
    if (sessionId && session) {
      trackAssessmentComplete(sessionId, '360');
    }
  }, [sessionId, session]);


  if (isLoading) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !session) {
    return (
      <Container>
        <Alert severity="error" sx={{ mt: 4 }}>
          Failed to load results. Please check the session ID and try again.
        </Alert>
      </Container>
    );
  }

  const sessionData = session.data as any;

  return (
    <Container maxWidth="md">
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
        <Typography variant="h2" component="h1" gutterBottom>
          {t('results.title')}
        </Typography>
        <Typography variant="h5" color="text.secondary" gutterBottom>
          {t('results.subtitle')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
          {t('results.message')}
        </Typography>
      </Box>


      {/* Simple completion info */}
      <Card sx={{ mb: 4 }}>
        <CardContent sx={{ textAlign: 'center' }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {t('results.contact')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('results.completed.on')}: {sessionData.completedAt ? formatCompletedDate(sessionData.completedAt) : new Date().toLocaleDateString()}
          </Typography>
        </CardContent>
      </Card>

      {/* CTA Button */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Button
          variant="contained"
          size="large"
          href="https://federman.org.il"
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            px: 4,
            py: 1.5,
            borderRadius: 2,
            textTransform: 'none',
            fontSize: '1.1rem',
          }}
        >
          {t('results.learnMore')}
        </Button>
      </Box>
    </Container>
  );
};

export default ResultsPage;